import React, { useEffect, useRef, useState } from 'react';

const BOAT_WIDTH = 140;
const SPEED = 0.4;

export default function WalkingPanda() {
  const initX = typeof window !== 'undefined' ? window.innerWidth * 0.3 : 200;
  const [x, setX] = useState(initX);
  const [facingRight, setFacingRight] = useState(true);
  const [isMoving, setIsMoving] = useState(true);
  const [pupil, setPupil] = useState({ x: 0, y: 0 });

  const xRef = useRef(initX);
  const dirRef = useRef(1);
  const isMovingRef = useRef(true);
  const rafRef = useRef(null);
  const pandaRef = useRef(null);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!pandaRef.current) return;
      const rect = pandaRef.current.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const angle = Math.atan2(e.clientY - cy, e.clientX - cx);
      const r = 2.5;
      setPupil({ x: Math.cos(angle) * r, y: Math.sin(angle) * r });
    };
    window.addEventListener('mousemove', onMouseMove);
    return () => window.removeEventListener('mousemove', onMouseMove);
  }, []);

  useEffect(() => {
    let pauseTimeout = null;

    const step = () => {
      if (isMovingRef.current) {
        xRef.current += dirRef.current * SPEED;
        const maxX = window.innerWidth - BOAT_WIDTH - 10;

        if (xRef.current >= maxX) {
          xRef.current = maxX;
          dirRef.current = -1;
          setFacingRight(false);
        } else if (xRef.current <= 10) {
          xRef.current = 10;
          dirRef.current = 1;
          setFacingRight(true);
        }

        if (Math.random() < 0.0006) {
          isMovingRef.current = false;
          setIsMoving(false);
          pauseTimeout = setTimeout(() => {
            isMovingRef.current = true;
            setIsMoving(true);
            if (Math.random() < 0.4) {
              dirRef.current *= -1;
              setFacingRight(dirRef.current === 1);
            }
          }, 1500 + Math.random() * 3000);
        }

        setX(xRef.current);
      }
      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(rafRef.current);
      if (pauseTimeout) clearTimeout(pauseTimeout);
    };
  }, []);

  const px = pupil.x;
  const py = pupil.y;

  return (
    <>
      <style>{`
        @keyframes boatFloat {
          0%, 100% { transform: translateY(0px)  rotate(-1.4deg); }
          50%       { transform: translateY(-6px) rotate(1.4deg);  }
        }
        @keyframes boatIdle {
          0%, 100% { transform: translateY(0px)  rotate(-0.7deg); }
          50%       { transform: translateY(-3px) rotate(0.7deg);  }
        }
        .boat-g {
          transform-box: fill-box;
          transform-origin: 50% 78%;
        }
      `}</style>
      <div
        ref={pandaRef}
        style={{
          position: 'fixed',
          bottom: 0,
          left: x,
          zIndex: 9998,
          pointerEvents: 'none',
          transform: `scaleX(${facingRight ? 1 : -1})`,
          transition: 'transform 0.3s ease',
          filter: 'drop-shadow(0 8px 18px rgba(0,0,0,0.15))',
        }}
      >
        <svg width="140" height="96" viewBox="0 0 140 96" xmlns="http://www.w3.org/2000/svg">
          <g
            className="boat-g"
            style={{
              animation: isMoving
                ? 'boatFloat 2s ease-in-out infinite'
                : 'boatIdle 3.2s ease-in-out infinite',
            }}
          >
            {/* Water shimmer under hull */}
            <ellipse cx="70" cy="93" rx="58" ry="4.5" fill="rgba(96,165,250,0.15)" />

            {/* Hull exterior */}
            <path d="M 15 63 Q 70 91 125 63 L 121 54 Q 70 78 19 54 Z" fill="#7B4F2E" />

            {/* Hull grain lines */}
            <path d="M 22 70 Q 70 87 118 70" stroke="#5C3A1A" strokeWidth="1.5" fill="none" opacity="0.5" />
            <path d="M 18 76 Q 70 93 122 76" stroke="#5C3A1A" strokeWidth="1" fill="none" opacity="0.3" />

            {/* Panda body — sits inside, covered by rim */}
            <ellipse cx="70" cy="66" rx="20" ry="18" fill="white" />

            {/* Boat rim — overlaps panda body to sell the "inside" look */}
            <ellipse cx="70" cy="57" rx="52" ry="11.5" fill="#A0703C" />
            {/* Rim top highlight */}
            <path d="M 21 54 Q 70 63 119 54" stroke="#C8924E" strokeWidth="5" strokeLinecap="round" fill="none" />
            {/* Boat interior (dark hollow) */}
            <ellipse cx="70" cy="58" rx="43" ry="7.5" fill="#3C1E08" />

            {/* Left arm resting on rim */}
            <ellipse cx="26" cy="55" rx="12" ry="6.5" fill="#1a1a1a" transform="rotate(-14 26 55)" />
            {/* Right arm resting on rim */}
            <ellipse cx="114" cy="55" rx="12" ry="6.5" fill="#1a1a1a" transform="rotate(14 114 55)" />

            {/* Oar — handle */}
            <rect x="106" y="38" width="4" height="42" rx="2" fill="#8B5E3C"
              transform="rotate(28 106 38)" />
            {/* Oar — blade */}
            <path d="M 126 68 Q 138 63 136 78 Q 126 82 126 68 Z" fill="#A67C52" />

            {/* Ears (behind head) */}
            <circle cx="51" cy="15" r="9.5" fill="#1a1a1a" />
            <circle cx="89" cy="15" r="9.5" fill="#1a1a1a" />

            {/* Head */}
            <circle cx="70" cy="33" r="23" fill="white" />

            {/* Eye patches */}
            <ellipse cx="59" cy="28" rx="10" ry="9" fill="#1a1a1a" />
            <ellipse cx="81" cy="28" rx="10" ry="9" fill="#1a1a1a" />
            {/* Eye whites */}
            <circle cx="59" cy="28" r="6"   fill="white" />
            <circle cx="81" cy="28" r="6"   fill="white" />
            {/* Pupils — cursor tracking */}
            <circle cx={59 + px} cy={28 + py} r="3.5" fill="#1a1a1a" />
            <circle cx={81 + px} cy={28 + py} r="3.5" fill="#1a1a1a" />
            {/* Eye shine */}
            <circle cx={59 + px + 1.5} cy={28 + py - 1.5} r="1.2" fill="white" />
            <circle cx={81 + px + 1.5} cy={28 + py - 1.5} r="1.2" fill="white" />

            {/* Nose */}
            <ellipse cx="70" cy="38" rx="4" ry="2.8" fill="#333" />
            {/* Smile */}
            <path d="M 65 43 Q 70 47 75 43"
              stroke="#333" strokeWidth="1.5" fill="none" strokeLinecap="round" />
            {/* Blush */}
            <ellipse cx="51" cy="35" rx="5.5" ry="3" fill="#FFB3C1" opacity="0.45" />
            <ellipse cx="89" cy="35" rx="5.5" ry="3" fill="#FFB3C1" opacity="0.45" />
          </g>
        </svg>
      </div>
    </>
  );
}
